import { useStatsStore } from '../../store/statsStore.js'

interface StatsProps {
  onBack: () => void
}

/**
 * Career stats screen — ART_GUIDE_v2 §5.
 */
export function Stats({ onBack }: StatsProps) {
  const stats = useStatsStore()
  const overallAccuracy = stats.totalShots > 0
    ? Math.round((stats.totalHits / stats.totalShots) * 100)
    : 0

  const difficulties = ['easy', 'medium', 'hard'] as const
  const totalWins = difficulties.reduce((s, d) => s + stats.wins[d], 0)
  const totalLosses = difficulties.reduce((s, d) => s + stats.losses[d], 0)

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: 24,
      padding: 24,
    }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          alignSelf: 'flex-start',
          background: 'none',
          border: 'none',
          color: '#8DA9C4',
          fontSize: 14,
          cursor: 'pointer',
          padding: '4px 0',
        }}
      >
        ‹ MENU
      </button>

      <h2 style={{ color: '#F4B942', letterSpacing: 4, fontWeight: 800, fontSize: 28 }}>
        CAREER STATS
      </h2>

      {/* Top tiles */}
      <div style={{ display: 'flex', gap: 12, width: '100%', maxWidth: 560 }}>
        {[
          { label: 'Games Played', value: stats.gamesPlayed },
          { label: 'Win Streak', value: stats.currentStreak, sub: `Best: ${stats.bestStreak}` },
          { label: 'Accuracy', value: `${overallAccuracy}%` },
        ].map(tile => (
          <div key={tile.label} style={{
            flex: 1,
            backgroundColor: '#0F2A50',
            border: '1px solid rgba(141,169,196,0.28)',
            borderRadius: 8,
            padding: '16px 12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#EAF2F8' }}>{tile.value}</div>
            <div style={{ fontSize: 12, color: '#8DA9C4', marginTop: 4 }}>{tile.label}</div>
            {tile.sub && (
              <div style={{ fontSize: 11, color: '#8DA9C4', marginTop: 2 }}>{tile.sub}</div>
            )}
          </div>
        ))}
      </div>

      {/* W/L record per difficulty */}
      <div style={{
        backgroundColor: '#13315C',
        border: '2px solid #F4B942',
        borderRadius: 8,
        padding: '16px 24px',
        width: '100%',
        maxWidth: 560,
      }}>
        <div style={{ fontSize: 14, color: '#8DA9C4', marginBottom: 12, fontWeight: 600 }}>
          Win / Loss Record
        </div>
        {difficulties.map(d => {
          const w = stats.wins[d]
          const l = stats.losses[d]
          const total = w + l
          const pct = total > 0 ? (w / total) * 100 : 50
          return (
            <div key={d} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: '#EAF2F8', textTransform: 'capitalize' }}>{d}</span>
                <span style={{ fontSize: 13, color: '#EAF2F8' }}>{w}W – {l}L</span>
              </div>
              <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', backgroundColor: '#0F2A50' }}>
                {total > 0 && (
                  <>
                    <div style={{ width: `${pct}%`, backgroundColor: '#3DDC97', transition: 'width 300ms' }} />
                    <div style={{ width: `${100 - pct}%`, backgroundColor: '#C1292E', transition: 'width 300ms' }} />
                  </>
                )}
              </div>
            </div>
          )
        })}

        <div style={{ fontSize: 12, color: '#8DA9C4', marginTop: 8, textAlign: 'center' }}>
          Overall: {totalWins}W – {totalLosses}L
        </div>
      </div>
    </div>
  )
}
